
import { IAppointment } from '../../../../API/GoMoto';
import { GoMotoFunction, RequireDefined } from '../../../../API/GoMoto.types';
import { QRCodeScanner } from '../../../../context/hardware';
import { IconTypes } from '../../../../controls/Icon/GenericIcon/GenericIcon.types';

/**
 * The props object for the ScanQRCode component.
 */
export interface ScanQRCodeProps {
    /**
     * The callback function to navigate to the next page.
     */
    onProceed: GoMotoFunction<
        [RequireDefined<QRCodeScanner['result']>, IAppointment],
        void
    >;
    /**
     * The function to resend the QR Code when the QR Code is lost.
     */
    onResendClick: VoidFunction;
    /**
     * Whether we should set an error if the QR Code type is not for a pickup to indicate
     * that the user gave us the wrong QR Code.
     */
    validateAppointmentKey?: boolean;
    /**
     * The secondary heading localization text for the component heading.
     */
    secondaryHeading: string;
    /**
     * The primary heading localization text for the component heading.
     */
    primaryHeading: string;
    /**
     * What icon should be used for the heading component.
     */
    headingIconType: IconTypes;
    /**
     * What the accessibility aria-label for the icon should be for the heading component.
     */
    iconAriaLabel?: string;
}

/**
 * Enum for every type of QR Code Scanner error that can occur.
 */
export enum ScanError {
    /**
     * No QR Code Scanner error.
     */
    none = 0,
    /**
     * The getAppointment failed to retrieve an appointment object and this error is set.
     */
    appointmentNotFound,
    /**
     * This error is set when the kioskKey and appointment kioskKey do not match.
     */
    kioskDoesNotHavekey,
    /**
     * This error is set when the appointment object does not have a keySlot value.
     */
    keyNotLoaded,
    /**
     * This error is set when we cannot find a dealer that is associated with the kiosk's dealer.
     */
    dealerDoesNotHaveAppointment,
    /**
     * This error is set if the appointment retrieved has already been completed.
     */
    appointmentCompleted,
    /**
     * This error is set when the QR Code scanned is not for a pickup.
     */
    thisIsNotAVehiclePickupQrCode,
    /**
     * This error is set when the QR Code type is an unknown type and does not match our expected QR Code types.
     */
    NotAValidQrCode,
    /**
     * If a scanner error occurs and the error is not `ScannerNotFound`, this default error is set.
     */
    BadFormat,
    /**
     * This error is set if the kiosk scanner is in some way not able to be communicated with.
     */
    ScannerNotFound,
    /**
     * This error is set when the appointment has the status `scheduled` and an advisor did not manually store the key.
     */
    appointmentScheduled,
    /**
     * This error occurs when the appointment still has payment due.
     */
    notPaid
}